import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  AirflowConnection,
  DagCollection,
  DagListFilter,
  DagRunCollection,
  ImportErrorCollection,
  TaskInstanceCollection,
} from '../types';

/**
 * Client for the Airflow backend plugin (/api/airflow), which holds the API
 * credential, mints the JWT that Airflow 3's /api/v2 requires, and exposes a
 * read-only subset of endpoints. The browser never sees the Airflow token.
 */
export interface AirflowApi {
  getConnection(): Promise<AirflowConnection>;
  getDags(filter?: DagListFilter): Promise<DagCollection>;
  getDagRuns(
    dagId: string,
    params?: { limit?: number; order_by?: string },
  ): Promise<DagRunCollection>;
  getRecentRuns(params?: {
    limit?: number;
    order_by?: string;
  }): Promise<DagRunCollection>;
  getTaskInstances(
    dagId: string,
    runId: string,
  ): Promise<TaskInstanceCollection>;
  getImportErrors(params?: { limit?: number }): Promise<ImportErrorCollection>;
}

export const airflowApiRef = createApiRef<AirflowApi>({
  id: 'plugin.airflow.service',
});

const qs = (params?: Record<string, unknown>): string => {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
};

export class AirflowClientApi implements AirflowApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('airflow');
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!res.ok) {
      let message = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        message =
          body?.error?.message ?? body?.error ?? body?.detail ?? message;
      } catch {
        // non-JSON error body
      }
      throw new Error(message);
    }
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  getConnection(): Promise<AirflowConnection> {
    return this.request<AirflowConnection>('/health');
  }

  getDags(filter?: DagListFilter): Promise<DagCollection> {
    return this.request<DagCollection>(
      `/dags${qs({ limit: 100, order_by: 'dag_id', ...filter })}`,
    );
  }

  getDagRuns(
    dagId: string,
    params?: { limit?: number; order_by?: string },
  ): Promise<DagRunCollection> {
    return this.request<DagRunCollection>(
      `/dags/${encodeURIComponent(dagId)}/dagRuns${qs({
        limit: 10,
        order_by: '-run_after',
        ...params,
      })}`,
    );
  }

  getRecentRuns(params?: {
    limit?: number;
    order_by?: string;
  }): Promise<DagRunCollection> {
    return this.request<DagRunCollection>(
      `/dagRuns${qs({ limit: 20, order_by: '-run_after', ...params })}`,
    );
  }

  getTaskInstances(
    dagId: string,
    runId: string,
  ): Promise<TaskInstanceCollection> {
    return this.request<TaskInstanceCollection>(
      `/dags/${encodeURIComponent(dagId)}/dagRuns/${encodeURIComponent(
        runId,
      )}/taskInstances`,
    );
  }

  getImportErrors(params?: { limit?: number }): Promise<ImportErrorCollection> {
    return this.request<ImportErrorCollection>(
      `/importErrors${qs({ limit: 100, ...params })}`,
    );
  }
}
